"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { Search, X, Filter, Plus, Save } from "lucide-react";
import { toast, Toaster } from "react-hot-toast";

const AdminApplicationsView = () => {
  // Original state for applications
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    name: "",
    department: "",
  });
  const [departments, setDepartments] = useState([]);

  // New state for job creation
  const [showJobModal, setShowJobModal] = useState(false);
  const [jobFormData, setJobFormData] = useState({
    title: "",
    department: "",
    type: "Full-time",
    description: "",
    requirements: [""],
    responsibilities: [""],
  });

  // Add this after all the state declarations
  const handleApiCall = async (
    apiCall,
    loadingMessage,
    successMessage,
    errorMessage
  ) => {
    const loadingToast = toast.loading(loadingMessage);
    try {
      const result = await apiCall();
      toast.dismiss(loadingToast);
      toast.success(successMessage);
      return result;
    } catch (error) {
      console.error(error);
      toast.dismiss(loadingToast);
      toast.error(errorMessage || "An error occurred. Please try again.");
      return null;
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    // Apply filters whenever applications or filters change
    applyFilters();
  }, [applications, filters]);

  useEffect(() => {
    // Extract unique departments from applications
    if (applications.length > 0) {
      const uniqueDepartments = [
        ...new Set(applications.map((app) => app.department)),
      ];
      setDepartments(uniqueDepartments);
    }
  }, [applications]);

  const fetchApplications = async () => {
    const result = await handleApiCall(
      async () => {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/careers/applications`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        return response.data;
      },
      "Loading applications...",
      "Applications loaded successfully",
      "Failed to load applications"
    );

    if (result) {
      setApplications(result);
      setFilteredApplications(result);
    }
  };

  const handleDownloadCV = async (id, fileName) => {
    const result = await handleApiCall(
      async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/careers/download-cv/${id}`,
            {
              responseType: "blob",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          // Check if we got a valid blob response
          if (response.data.size === 0) {
            throw new Error("Empty file received");
          }

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();

          // Clean up
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 100);

          return true;
        } catch (error) {
          console.error("Error in CV download:", error);
          throw error; // Re-throw to be caught by handleApiCall
        }
      },
      // "Downloading CV...",
      "CV downloaded successfully!",
      "Failed to download CV. Please try again."
    );
  };

  const handleRowClick = (application) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      name: "",
      department: "",
    });
  };

  const applyFilters = () => {
    let filtered = [...applications];

    if (filters.name) {
      filtered = filtered.filter((app) =>
        app.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    if (filters.department) {
      filtered = filtered.filter(
        (app) => app.department === filters.department
      );
    }

    setFilteredApplications(filtered);
  };

  // Parse experiences JSON string to array
  const parseExperiences = (experiencesStr) => {
    try {
      return JSON.parse(experiencesStr);
    } catch (error) {
      console.error("Error parsing experiences:", error);
      return [];
    }
  };

  // New job creation functions
  const handleJobInputChange = (e) => {
    const { name, value } = e.target;
    setJobFormData({
      ...jobFormData,
      [name]: value,
    });
  };

  const handleRequirementChange = (index, value) => {
    const updatedRequirements = [...jobFormData.requirements];
    updatedRequirements[index] = value;
    setJobFormData({
      ...jobFormData,
      requirements: updatedRequirements,
    });
  };

  const handleResponsibilityChange = (index, value) => {
    const updatedResponsibilities = [...jobFormData.responsibilities];
    updatedResponsibilities[index] = value;
    setJobFormData({
      ...jobFormData,
      responsibilities: updatedResponsibilities,
    });
  };

  const addRequirementField = () => {
    setJobFormData({
      ...jobFormData,
      requirements: [...jobFormData.requirements, ""],
    });
  };

  const removeRequirementField = (index) => {
    const updatedRequirements = [...jobFormData.requirements];
    updatedRequirements.splice(index, 1);
    setJobFormData({
      ...jobFormData,
      requirements: updatedRequirements,
    });
  };

  const addResponsibilityField = () => {
    setJobFormData({
      ...jobFormData,
      responsibilities: [...jobFormData.responsibilities, ""],
    });
  };

  const removeResponsibilityField = (index) => {
    const updatedResponsibilities = [...jobFormData.responsibilities];
    updatedResponsibilities.splice(index, 1);
    setJobFormData({
      ...jobFormData,
      responsibilities: updatedResponsibilities,
    });
  };

  const resetJobForm = () => {
    setJobFormData({
      title: "",
      department: "",
      type: "Full-time",
      description: "",
      requirements: [""],
      responsibilities: [""],
    });
  };

  const handleAddJob = async (e) => {
    e.preventDefault();

    // Validate form
    if (
      !jobFormData.title ||
      !jobFormData.department ||
      !jobFormData.description ||
      jobFormData.requirements.some((req) => !req) ||
      jobFormData.responsibilities.some((resp) => !resp)
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    const jobData = {
      ...jobFormData,
      requirements: JSON.stringify(
        jobFormData.requirements.filter((req) => req.trim())
      ),
      responsibilities: JSON.stringify(
        jobFormData.responsibilities.filter((resp) => resp.trim())
      ),
    };

    const result = await handleApiCall(
      async () => {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/careers/job-listings`,
          jobData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        return true;
      },
      "Adding new job...",
      "Job added successfully!",
      "Failed to add job. Please try again."
    );

    if (result) {
      setShowJobModal(false);
      resetJobForm();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto bg-white shadow-md rounded-lg p-6">
        <div className="bg-blue-800 text-white text-lg font-semibold px-4 py-3 rounded-t mb-6 flex justify-between items-center">
          <h2>Job Applications</h2>
          <button
            onClick={() => setShowJobModal(true)}
            className="bg-white text-blue-800 px-4 py-1 rounded-md flex items-center text-sm font-medium"
          >
            <Plus size={18} className="mr-1" /> Add New Job
          </button>
        </div>

        {/* Filter Bar */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label
                htmlFor="nameFilter"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Search by Name
              </label>
              <div className="relative">
                <input
                  id="nameFilter"
                  type="text"
                  name="name"
                  value={filters.name}
                  onChange={handleFilterChange}
                  placeholder="Search applicants..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Search
                  className="absolute left-3 top-2.5 text-gray-400"
                  size={20}
                />
              </div>
            </div>

            <div className="flex-1">
              <label
                htmlFor="departmentFilter"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Filter by Department
              </label>
              <div className="relative">
                <select
                  id="departmentFilter"
                  name="department"
                  value={filters.department}
                  onChange={handleFilterChange}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Departments</option>
                  {departments.map((dept, index) => (
                    <option key={index} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
                <Filter
                  className="absolute left-3 top-2.5 text-gray-400"
                  size={20}
                />
              </div>
            </div>

            <button
              onClick={resetFilters}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-gray-600">
          Showing {filteredApplications.length} of {applications.length}{" "}
          applications
        </div>

        {/* Applications Table */}
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CV
                </th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Applied On
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map((app) => (
                <tr
                  key={app.id}
                  onClick={() => handleRowClick(app)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="py-3 px-4 border-b">{app.name}</td>
                  <td className="py-3 px-4 border-b">{app.email}</td>
                  <td className="py-3 px-4 border-b">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {app.department}
                    </span>
                  </td>
                  <td className="py-3 px-4 border-b">
                    {app.cv_url && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          handleDownloadCV(app.id, app.cv_url.split("/").pop());
                        }}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded text-sm"
                      >
                        Download CV
                      </button>
                    )}
                  </td>
                  <td className="py-3 px-4 border-b">
                    {new Date(app.applied_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {filteredApplications.length === 0 && (
                <tr>
                  <td colSpan="5" className="py-4 text-center text-gray-500">
                    No applications found matching your filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Application Detail Modal */}
        {showModal && selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="bg-blue-800 text-white px-4 py-3 flex justify-between items-center">
                <h3 className="text-xl font-bold">Applicant Details</h3>
                <button
                  onClick={closeModal}
                  className="text-white hover:text-gray-200"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Name</h4>
                    <p className="text-lg">{selectedApplication.name}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Email</h4>
                    <p className="text-lg">{selectedApplication.email}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      Department
                    </h4>
                    <p className="text-lg">{selectedApplication.department}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">
                      Applied On
                    </h4>
                    <p className="text-lg">
                      {new Date(
                        selectedApplication.applied_at
                      ).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3">
                    Work Experience
                  </h4>
                  {selectedApplication.experiences && (
                    <div className="space-y-3">
                      {parseExperiences(selectedApplication.experiences).map(
                        (exp, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded">
                            <p className="font-medium">
                              {exp.title} at {exp.company}
                            </p>
                            <p className="text-gray-600">{exp.years} years</p>
                          </div>
                        )
                      )}
                    </div>
                  )}
                  {(!selectedApplication.experiences ||
                    parseExperiences(selectedApplication.experiences).length ===
                      0) && (
                    <p className="text-gray-500">
                      No experience information provided
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  {selectedApplication.cv_url && (
                    <button
                      onClick={() =>
                        handleDownloadCV(
                          selectedApplication.id,
                          selectedApplication.cv_url.split("/").pop()
                        )
                      }
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                    >
                      Download CV
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Job Modal */}
        {showJobModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="bg-blue-800 text-white px-4 py-3 flex justify-between items-center">
                <h3 className="text-xl font-bold">Add New Job Position</h3>
                <button
                  onClick={() => {
                    setShowJobModal(false);
                    resetJobForm();
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6">
                <form onSubmit={handleAddJob}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Job Title*
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={jobFormData.title}
                        onChange={handleJobInputChange}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Department*
                      </label>
                      <input
                        type="text"
                        name="department"
                        value={jobFormData.department}
                        onChange={handleJobInputChange}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job Type*
                    </label>
                    <select
                      name="type"
                      value={jobFormData.type}
                      onChange={handleJobInputChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description*
                    </label>
                    <textarea
                      name="description"
                      value={jobFormData.description}
                      onChange={handleJobInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    ></textarea>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Requirements*
                      </label>
                      <button
                        type="button"
                        onClick={addRequirementField}
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                      >
                        <Plus size={16} className="mr-1" /> Add Requirement
                      </button>
                    </div>
                    {jobFormData.requirements.map((requirement, index) => (
                      <div key={index} className="flex items-center mb-2">
                        <input
                          type="text"
                          value={requirement}
                          onChange={(e) =>
                            handleRequirementChange(index, e.target.value)
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter requirement"
                          required
                        />
                        {jobFormData.requirements.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRequirementField(index)}
                            className="ml-2 text-red-500 hover:text-red-700"
                          >
                            <X size={20} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Responsibilities*
                      </label>
                      <button
                        type="button"
                        onClick={addResponsibilityField}
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                      >
                        <Plus size={16} className="mr-1" /> Add Responsibility
                      </button>
                    </div>
                    {jobFormData.responsibilities.map(
                      (responsibility, index) => (
                        <div key={index} className="flex items-center mb-2">
                          <input
                            type="text"
                            value={responsibility}
                            onChange={(e) =>
                              handleResponsibilityChange(index, e.target.value)
                            }
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter responsibility"
                            required
                          />
                          {jobFormData.responsibilities.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeResponsibilityField(index)}
                              className="ml-2 text-red-500 hover:text-red-700"
                            >
                              <X size={20} />
                            </button>
                          )}
                        </div>
                      )
                    )}
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowJobModal(false);
                        resetJobForm();
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                      <Save size={18} className="mr-1" />
                      Save Job
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      <Toaster position="top-right" />
    </div>
  );
};

export default AdminApplicationsView;
